import { sharedLocalDevice, rememberSharedStatus, BROKER_ORIGIN } from './shared-device.mjs';
import { signedMultipart } from './device-multipart.mjs';
// Browser key possession, not hardware/employee attestation. No private-key export.
export function canonical(value, depth = 0) {
  let remaining=65536,nodes=0;
  const add=text=>{remaining-=new TextEncoder().encode(text).length;if(remaining<0)throw new Error('device_body_bound');return text;};
  // KRE-92: sign the same value the server parses from axios's JSON.stringify body — toJSON applied,
  // undefined/function/symbol keys dropped, those in arrays and non-finite numbers sent as null.
  const prep=(x,k)=>x&&typeof x==='object'&&typeof x.toJSON==='function'?x.toJSON(k):x;
  const absent=x=>x===undefined||typeof x==='function'||typeof x==='symbol';
  const walk=(v,d)=>{
    if(d>16||++nodes>4096)throw new Error('device_input');
    if(typeof v==='string'){if(new TextEncoder().encode(v).length>65536)throw new Error('device_body_bound');return add(JSON.stringify(v));}
    if(typeof v==='number'&&!Number.isFinite(v))return add('null');
    if(v===null||typeof v==='boolean'||typeof v==='number')return add(JSON.stringify(v));
    if(Array.isArray(v)){if(v.length>1024)throw new Error('device_input');return add('[')+v.map((x,i)=>{const y=prep(x,String(i));return (i?add(','):'')+walk(absent(y)?null:y,d+1);}).join('')+add(']');}
    if(v&&Object.getPrototypeOf(v)===Object.prototype){const entries=[];for(const k in v){if(Object.prototype.hasOwnProperty.call(v,k)){const y=prep(v[k],k);if(absent(y))continue;entries.push([k,y]);if(entries.length>256)throw new Error('device_input');}}return add('{')+entries.sort(([a],[b])=>a<b?-1:a>b?1:0).map(([k,y],i)=>(i?add(','):'')+add(JSON.stringify(k))+add(':')+walk(y,d+1)).join('')+add('}');}
    throw new Error('device_input');
  };
  return walk(value,depth);
}
export function base64(bytes) { return btoa(String.fromCharCode(...new Uint8Array(bytes))); }
async function keyStore(mode, action) {
  const db = await new Promise((resolve, reject) => {
    const r = indexedDB.open('naezipscan-approved-device-v1', 1);
    r.onupgradeneeded = () => r.result.createObjectStore('keys');
    r.onsuccess = () => resolve(r.result); r.onerror = () => reject(new Error('device_storage'));
  });
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction('keys', mode); const r = action(tx.objectStore('keys'));
      tx.oncomplete = () => resolve(r.result); tx.onabort = tx.onerror = () => reject(new Error('device_storage'));
    });
  } finally { db.close(); }
}
export async function masterLocalDevice() { return keyStore('readonly', s => s.get('current')); }
export async function localDevice() { return localStorage.getItem?.('admin_device_shared_hint') === 'true' && window.location.origin !== BROKER_ORIGIN ? sharedLocalDevice() : masterLocalDevice(); }
export async function selectLocalDevice(id) { const key = await keyStore('readonly', s => s.get(id)); if (!key) throw new Error('device_key_not_local'); await keyStore('readwrite', s => s.put(key, 'current')); }
export async function forgetLocalDevice() { await keyStore('readwrite', s => s.delete('current')); }
export async function registerDevice(api, label, otp) {
  if (!window.isSecureContext || !crypto.subtle) throw new Error('device_https_required');
  const { data: status } = await api.get('/admin-devices/status', { _deviceBootstrap: true });
  rememberSharedStatus(status);
  if (status.shared_enabled === true && window.location.origin !== BROKER_ORIGIN) throw new Error('device_register_at_common_origin');
  const pair = await crypto.subtle.generateKey({ name: 'ECDSA', namedCurve: 'P-256' }, false, ['sign', 'verify']);
  const public_key = base64(await crypto.subtle.exportKey('spki', pair.publicKey));
  const { data: c } = await api.post('/admin-devices/enrollment-challenge', { label, public_key }, { _deviceBootstrap: true });
  const signature = base64(await crypto.subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, pair.privateKey, new TextEncoder().encode(c.signing_text)));
  const { data: enrolled } = await api.post('/admin-devices/enroll', { challenge_id: c.challenge_id, nonce: c.nonce, signature, otp }, { _deviceBootstrap: true });
  const saved = { device_id: enrolled.device_id, privateKey: pair.privateKey, key_fingerprint: enrolled.key_fingerprint, actor_user_no: status.actor_user_no };
  const current = await masterLocalDevice();
  await keyStore('readwrite', s => { s.put(saved, enrolled.device_id); return s.put(status.shared_enabled===true ? (current?.actor_user_no===status.actor_user_no ? current : saved) : current || saved, 'current'); });
  return enrolled;
}
// otp 가 null 이면 '방금 OTP 로그인' 인증으로 연결한다(서버가 세션 생성 2분 안 첫 연결만 허용, KRE-17).
export async function bindLocalDevice(api, id, otp) {
  const key = localStorage.getItem?.('admin_device_shared_hint') === 'true' && window.location.origin !== BROKER_ORIGIN ? await sharedLocalDevice() : await keyStore('readonly', s => s.get(id));
  if (!key || key.device_id !== id) throw new Error('device_key_not_local');
  const { data: c } = await api.post('/admin-devices/bind-challenge', { device_id: id, ...(key.site_certificate ? {site_certificate:key.site_certificate} : {}) }, { _deviceBootstrap: true });
  const signature = base64(await crypto.subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, key.privateKey, new TextEncoder().encode(c.signing_text)));
  const result = await api.post('/admin-devices/bind', { device_id: id, challenge_id: c.challenge_id, nonce: c.nonce, signature, ...(otp === null ? {login:true} : {otp}), ...(key.site_certificate ? {site_certificate:key.site_certificate} : {}) }, { _deviceBootstrap: true });
  if (!key.site_certificate) await keyStore('readwrite', s => s.put(key, 'current'));
  return result.data;
}
// 로그인 성공 직후 호출: 이 브라우저에 승인된 기기(신관리자) 또는 v2 사이트 연결 키(다른 사이트)가 있으면
// 이 로그인을 OTP 재입력 없이 연결한다. 실패해도 로그인은 그대로이고, 기기 화면에서 다시 연결할 수 있다.
// 스위치(전체 차단·위임) 힌트를 서버 값에 맞춘다. 화면 진입·로그인마다 호출 — 스위치를 DB 에서 바꾸면
// 새로고침으로 반영된다(KRE-17). 힌트는 안내용이고 판정은 서버가 한다.
export async function syncDeviceHints(api) {
  try {
    const { data: status } = await api.get('/admin-devices/status', { _deviceBootstrap: true });
    if (!status || typeof status !== 'object') return null;
    rememberSharedStatus(status);
    localStorage.setItem('admin_device_enforcement_hint', String(status.enforcement_enabled === true));
    localStorage.setItem('admin_device_delegation_hint', String(status.delegation_enabled === true));
    return status;
  } catch { return null; }
}
// 페이지마다 첫 요청 전에 한 번만 스위치·공유 기기 문맥을 맞춘다. 차단을 켠 직후 새 탭의 첫 요청이 상태를 읽기 전에
// 서명 없이 나가 거절되고, 구관리자·엑스레이는 그걸 로그아웃으로 처리했다(KRE-17 2026-10-27 차단 테스트).
let hintsOnce = null;
export function ensureDeviceHints(api) {
  if (!hintsOnce) hintsOnce = syncDeviceHints(api);
  return hintsOnce;
}
// 로그인 화면이 기다리는 시간은 최대 timeoutMs — 늦으면 연결 없이 넘어가고 기기 화면에서 다시 연결한다.
export async function bindAfterLogin(api, timeoutMs = 5000) {
  let timer;
  const limit = new Promise(resolve => { timer = setTimeout(() => resolve(false), timeoutMs); });
  try { return await Promise.race([bindAfterLoginNow(api), limit]); } finally { clearTimeout(timer); }
}
async function bindAfterLoginNow(api) {
  try {
    const status = await syncDeviceHints(api);
    if (status?.registration_enabled !== true) return false;
    const child = status.shared_enabled === true && window.location.origin !== BROKER_ORIGIN;
    const key = child ? await sharedLocalDevice() : await masterLocalDevice();
    if (!key || typeof key.device_id !== 'string' || (child ? !key.site_certificate : key.actor_user_no !== status.actor_user_no)) return false;
    await bindLocalDevice(api, key.device_id, null);
    await syncDeviceHints(api); // 연결된 세션은 세션 정책에서 차단 대상이 될 수 있다.
    return true;
  } catch { return false; }
}
export async function attachDeviceProof(config, api) {
  // 상태 조회는 서버도 증명을 요구하지 않는 부트스트랩 경로다(힌트 확인이 자기 자신을 기다리지 않게).
  if (config._deviceBootstrap || String(config.url || '').endsWith('/admin-devices/status')) return config;
  if (typeof api?.get === 'function') await ensureDeviceHints(api);
  if (localStorage.admin_device_enforcement_hint !== 'true') return config;
  const method = String(config.method || 'get').toUpperCase();
  const uri = new URL(api.getUri(config), window.location.origin);
  const expected = new URL(api.defaults?.baseURL || '/', window.location.origin);
  if (uri.origin !== expected.origin) throw new Error('device_foreign_target');
  const path = uri.pathname + uri.search;
  const bootstrap = new Set(['POST /api/v1/user-login','POST /api/v1/user-login/qrcode','POST /api/v1/user-login/mfa','GET /api/v1/admin-devices','GET /api/v1/admin-devices/status','POST /api/v1/admin-devices/browser-status','POST /api/v1/admin-devices/enrollment-challenge','POST /api/v1/admin-devices/enroll','POST /api/v1/admin-devices/request-nonce','POST /api/v1/admin-devices/bind-challenge','POST /api/v1/admin-devices/bind','POST /api/v1/admin-devices/site-challenge','POST /api/v1/admin-devices/site-inspect','POST /api/v1/admin-devices/site-complete']);
  if (bootstrap.has(method + ' ' + uri.pathname)) return config;
  const device = await localDevice(); if (!device) return config; // Server remains authoritative.
  if (['GET','HEAD'].includes(method) && config.data != null && (Object.getPrototypeOf(config.data) !== Object.prototype || Object.keys(config.data).length)) throw new Error('device_get_body');
  let body_hash;
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    const packed = await signedMultipart(config.data, method, path);
    config.data = packed.blob; config.headers = config.headers || {};
    config.headers['Content-Type'] = packed.content_type;
    config.headers['X-Admin-Device-Body-Sha256'] = packed.body_hash;
    body_hash = packed.body_hash;
  } else {
    const body = ['GET','HEAD'].includes(method) ? '' : canonical(config.data ?? {});
    if (new TextEncoder().encode(body).length > 65536) throw new Error('device_body_bound');
    const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(body));
    body_hash = Array.from(new Uint8Array(hash), v => v.toString(16).padStart(2,'0')).join('');
  }
  const { data: c } = await api.post('/admin-devices/request-nonce', { device_id: device.device_id, method, path, body_hash, ...(device.site_certificate ? {site_certificate:device.site_certificate} : {}) }, { _deviceBootstrap: true });
  const signature = base64(await crypto.subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, device.privateKey, new TextEncoder().encode(c.signing_text)));
  config.headers = config.headers || {};
  config.headers['X-Admin-Device-Proof'] = base64(new TextEncoder().encode(JSON.stringify({ device_id: device.device_id, challenge_id: c.challenge_id, nonce: c.nonce, signature, ...(device.site_certificate ? {site_certificate:device.site_certificate} : {}) })));
  return config;
}
